import React, { Component } from "react";
import { withAuth } from "../Auth/WithAuth";
import { authenticatedFetch } from "../../utils/apiClient";
import FadeIn from "react-fade-in";
import LoadAnim from "../App/LoadAnim.js";
import "./Admin.css";

let isMounted = false;

export default withAuth(
  class SeatmapAdmin extends Component {
    constructor(props) {
      super(props);
      this.state = {
        rows: [],
        loading: true,
        hoveredSeat: null,
        selectedSeat: null,
        showInfoModal: false,
        infoModalSeat: null,
        editMode: false,
        editModeType: 'hide', // 'hide' or 'block'
        pendingChanges: new Set()
      };

      this.getSeatDetails = this.getSeatDetails.bind(this);
      this.handleSeatHover = this.handleSeatHover.bind(this);
      this.handleSeatClick = this.handleSeatClick.bind(this);
      this.updateSeats = this.updateSeats.bind(this);
      this.closeInfoModal = this.closeInfoModal.bind(this);
      this.toggleEditMode = this.toggleEditMode.bind(this);
      this.toggleEditModeType = this.toggleEditModeType.bind(this);
      this.toggleSeatVisibility = this.toggleSeatVisibility.bind(this);
      this.toggleSeatBlocking = this.toggleSeatBlocking.bind(this);
      this.saveLayoutChanges = this.saveLayoutChanges.bind(this);
      this.discardChanges = this.discardChanges.bind(this);
    }

    async componentDidMount() {
      isMounted = true;
      await this.getSeatDetails();
      this.interval = setInterval(() => this.updateSeats(), 5000);
    }

    componentWillUnmount() {
      isMounted = false;
      clearInterval(this.interval);
    }

    async updateSeats() {
      if (isMounted) {
        await this.getSeatDetails();
      }
    }

    async getSeatDetails() {
      try {
        // Get users exactly like SignupList does
        const usersResponse = await authenticatedFetch("/api/admin/getpersons", {
          method: "GET",
          headers: {
            Accept: "application/json",
            pragma: "no-cache",
            "cache-control": "no-cache"
          }
        });

        // Get ALL seats for admin view (including hidden ones)
        const seatsResponse = await authenticatedFetch("/api/admin/getAllSeats", {
          method: "GET",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            pragma: "no-cache",
            "cache-control": "no-cache"
          }
        });

        if (usersResponse.ok && seatsResponse.ok) {
          const users = await usersResponse.json();
          const seats = await seatsResponse.json();

          console.log("Users sample:", users.slice(0, 2));
          console.log("Seats sample:", seats.slice(0, 2));

          // Create lookup map by seat position like SignupList shows
          const userBySeat = {};
          users.forEach(user => {
            if (user.rowNum && user.seatNum) {
              const key = `${user.rowNum}-${user.seatNum}`;
              userBySeat[key] = user;
            }
          });

          // Combine seat data with user data
          const combinedData = seats.map(seat => {
            const key = `${seat.rownum}-${seat.number}`;
            const user = userBySeat[key];

            return {
              ...seat,
              userInfo: user || null,
              isHidden: seat.isHidden || false,
              isBlocked: seat.isBlocked || false
            };
          });

          console.log("Paid users found:", users.filter(u => u.paid === true));
          console.log("Combined data sample:", combinedData.slice(0, 3));

          this.setState({
            rows: combinedData,
            loading: false
          });
        } else {
          console.error("Failed to fetch data");
          this.setState({ loading: false });
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        this.setState({ loading: false });
      }
    }

    handleSeatHover(seat) {
      if (seat && seat.userInfo) {
        console.log(`Hovered seat ${seat.rownum}-${seat.number}:`, {
          paid: seat.userInfo.paid,
          paidType: typeof seat.userInfo.paid,
          firstName: seat.userInfo.firstName,
          lastName: seat.userInfo.lastName
        });
      }
      this.setState({ hoveredSeat: seat });
    }

    handleSeatClick(seat) {
      if (this.state.editMode) {
        // In edit mode, toggle seat visibility or blocking based on mode type
        if (this.state.editModeType === 'hide') {
          this.toggleSeatVisibility(seat);
        } else {
          this.toggleSeatBlocking(seat);
        }
      } else if (seat && seat.userInfo) {
        // In normal mode, show info modal
        this.setState({
          showInfoModal: true,
          infoModalSeat: seat
        });
      }
    }

    closeInfoModal() {
      this.setState({
        showInfoModal: false,
        infoModalSeat: null
      });
    }

    toggleEditMode() {
      this.setState(prevState => ({
        editMode: !prevState.editMode,
        pendingChanges: new Set() // Clear pending changes when toggling edit mode
      }));
    }

    toggleEditModeType() {
      this.setState(prevState => ({
        editModeType: prevState.editModeType === 'hide' ? 'block' : 'hide',
        pendingChanges: new Set() // Clear pending changes when switching mode type
      }));
    }

    toggleSeatVisibility(seat) {
      // Prevent hiding seats that are already reserved or held
      if (seat.isReserved === "yes" || seat.isReserved === "held") {
        alert("Kan ikke skjule plasser som er reservert eller holdt!");
        return;
      }

      const seatKey = `${seat.rownum}-${seat.number}`;
      const newPendingChanges = new Set(this.state.pendingChanges);

      // If seat is already in pending changes, remove it (cancel the change)
      if (newPendingChanges.has(seatKey)) {
        newPendingChanges.delete(seatKey);
      } else {
        // If seat is not in pending changes, add it
        // This will either mark it for hiding (if currently visible) or unhiding (if currently hidden)
        newPendingChanges.add(seatKey);
      }

      this.setState({ pendingChanges: newPendingChanges });
    }

    toggleSeatBlocking(seat) {
      // Prevent blocking seats that are already reserved or held
      if (seat.isReserved === "yes" || seat.isReserved === "held") {
        alert("Kan ikke blokkere plasser som er reservert eller holdt!");
        return;
      }

      const seatKey = `${seat.rownum}-${seat.number}`;
      const newPendingChanges = new Set(this.state.pendingChanges);

      // If seat is already in pending changes, remove it (cancel the change)
      if (newPendingChanges.has(seatKey)) {
        newPendingChanges.delete(seatKey);
      } else {
        // If seat is not in pending changes, add it
        // This will either mark it for blocking (if currently unblocked) or unblocking (if currently blocked)
        newPendingChanges.add(seatKey);
      }

      this.setState({ pendingChanges: newPendingChanges });
    }

    async saveLayoutChanges() {
      if (this.state.pendingChanges.size === 0) {
        alert("Ingen endringer å lagre.");
        return;
      }

      try {
        const seatUpdates = Array.from(this.state.pendingChanges).map(seatKey => {
          const [rownum, number] = seatKey.split('-');

          // Find the seat in current data to determine its current state
          const seat = this.state.rows.find(s => s.rownum === parseInt(rownum, 10) && s.number === parseInt(number, 10));

          if (this.state.editModeType === 'hide') {
            const isCurrentlyHidden = seat ? seat.isHidden === true : false;
            return {
              rownum: parseInt(rownum, 10),
              number: parseInt(number, 10),
              isHidden: !isCurrentlyHidden // Toggle the hidden state
            };
          } else {
            const isCurrentlyBlocked = seat ? seat.isBlocked === true : false;
            return {
              rownum: parseInt(rownum, 10),
              number: parseInt(number, 10),
              isBlocked: !isCurrentlyBlocked // Toggle the blocked state
            };
          }
        });

        const response = await authenticatedFetch("/api/admin/updateSeatLayout", {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json"
          },
          body: JSON.stringify({ seatUpdates })
        });

        if (response.ok) {
          alert("Layoutendringer lagret!");
          this.setState({
            pendingChanges: new Set(),
            editMode: false
          });
          await this.getSeatDetails(); // Refresh data
        } else {
          alert("Feil ved lagring av endringer.");
        }
      } catch (err) {
        console.error("Error saving layout changes:", err);
        alert("Feil ved lagring av endringer.");
      }
    }

    discardChanges() {
      this.setState({
        pendingChanges: new Set(),
        editMode: false
      });
    }

    getSeatClasses(seat) {
      const seatKey = `${seat.rownum}-${seat.number}`;
      let classes = "btn seatBox ";

      const isPendingChange = this.state.pendingChanges.has(seatKey);
      const isCurrentlyHidden = seat.isHidden === true;
      const isCurrentlyBlocked = seat.isBlocked === true;

      if (this.state.editModeType === 'hide') {
        // Handle hidden/pending states
        if (isCurrentlyHidden && isPendingChange) {
          // Currently hidden, pending unhide - show as if it will be visible
          if (seat.isReserved === "no") {
            classes += "btn-info open ";
          } else if (seat.isReserved === "yes" && seat.userInfo && (seat.userInfo.paid === true || seat.userInfo.paid === "true")) {
            classes += "paid ";
          } else if (seat.isReserved === "yes") {
            classes += "taken ";
          } else if (seat.isReserved === "held") {
            classes += "held ";
          }
        } else if (isCurrentlyHidden && !isPendingChange) {
          // Currently hidden, no pending change
          classes += "hidden-seat ";
        } else if (!isCurrentlyHidden && isPendingChange) {
          // Currently visible, pending hide
          classes += "hidden-seat ";
        } else {
          // Currently visible, no pending change - normal seat coloring
          if (isCurrentlyBlocked) {
            classes += "blocked-seat ";
          } else if (seat.isReserved === "no") {
            classes += "btn-info open ";
          } else if (seat.isReserved === "yes" && seat.userInfo && (seat.userInfo.paid === true || seat.userInfo.paid === "true")) {
            classes += "paid ";
          } else if (seat.isReserved === "yes") {
            classes += "taken ";
          } else if (seat.isReserved === "held") {
            classes += "held ";
          }
        }
      } else {
        // Handle blocked/pending states
        if (isCurrentlyBlocked && isPendingChange) {
          // Currently blocked, pending unblock - show as if it will be unblocked
          if (seat.isReserved === "no") {
            classes += "btn-info open ";
          } else if (seat.isReserved === "yes" && seat.userInfo && (seat.userInfo.paid === true || seat.userInfo.paid === "true")) {
            classes += "paid ";
          } else if (seat.isReserved === "yes") {
            classes += "taken ";
          } else if (seat.isReserved === "held") {
            classes += "held ";
          }
        } else if (isCurrentlyBlocked && !isPendingChange) {
          // Currently blocked, no pending change
          classes += "blocked-seat ";
        } else if (!isCurrentlyBlocked && isPendingChange) {
          // Currently unblocked, pending block
          classes += "blocked-seat ";
        } else {
          // Currently unblocked, no pending change - normal seat coloring
          if (isCurrentlyHidden) {
            classes += "hidden-seat ";
          } else if (seat.isReserved === "no") {
            classes += "btn-info open ";
          } else if (seat.isReserved === "yes" && seat.userInfo && (seat.userInfo.paid === true || seat.userInfo.paid === "true")) {
            classes += "paid ";
          } else if (seat.isReserved === "yes") {
            classes += "taken ";
          } else if (seat.isReserved === "held") {
            classes += "held ";
          }
        }
      }

      // Add edit mode styling for seats that can be edited (not reserved or held)
      if (this.state.editMode && seat.isReserved !== "yes" && seat.isReserved !== "held") {
        classes += "editable ";
      }

      return classes;
    }

    render() {
      if (this.props.authenticated === null) return null;

      let rows = this.state.rows;

      // Group seats by row number
      const rowsData = {};
      if (rows && rows.length > 0) {
        for (let i = 0; i < rows.length; i++) {
          const rowNum = rows[i].rownum;
          if (!rowsData[rowNum]) {
            rowsData[rowNum] = [];
          }
          rowsData[rowNum].push(rows[i]);
        }
      }

      // Hardcoded row variables for admin - show ALL rows including hidden seats
      const row1 = rowsData[1] || [];
      const row2 = rowsData[2] || [];
      const row3 = rowsData[3] || [];
      const row4 = rowsData[4] || [];
      const row5 = rowsData[5] || [];
      const row6 = rowsData[6] || [];
      const row7 = rowsData[7] || [];
      const row8 = rowsData[8] || [];
      const row9 = rowsData[9] || [];
      const row10 = rowsData[10] || [];

      return (
        <div className="SignupList">
          <h1>Plasskart oversikt</h1>
          <button
            className="btn btno btn-success edit"
            onClick={this.getSeatDetails}
            style={{ marginRight: '10px' }}
          >
            Refresh
          </button>

          <button
            className={`btn btno edit ${this.state.editMode ? 'btn-warning' : 'btn-primary'}`}
            onClick={this.toggleEditMode}
            style={{ marginRight: '10px' }}
          >
            {this.state.editMode ? 'Avbryt redigering' : 'Rediger layout'}
          </button>

          {this.state.editMode && (
            <button
              className={`btn btno edit ${this.state.editModeType === 'hide' ? 'btn-info' : 'btn-warning'}`}
              onClick={this.toggleEditModeType}
              style={{ marginRight: '10px' }}
            >
              Modus: {this.state.editModeType === 'hide' ? 'Skjul plasser' : 'Blokker plasser'}
            </button>
          )}

          {this.state.editMode && (
            <div>
              <button
                className="btn btno btn-success edit"
                onClick={this.saveLayoutChanges}
                style={{ marginRight: '10px' }}
                disabled={this.state.pendingChanges.size === 0}
              >
                Lagre endringer ({this.state.pendingChanges.size})
              </button>
              <button
                className="btn btno btn-secondary edit"
                onClick={this.discardChanges}
              >
                Forkast endringer
              </button>
            </div>
          )}

          <hr />

          {this.state.editMode && (
            <div style={{
              backgroundColor: '#fff3cd',
              border: '1px solid #ffeaa7',
              borderRadius: '4px',
              padding: '10px',
              marginBottom: '15px',
              color: '#856404'
            }}>
              <strong>Layout redigeringsmodus:</strong> Klikk på ledige plasser for å {this.state.editModeType === 'hide' ? 'skjule/vise' : 'blokkere/avblokkere'} dem.
              {this.state.editModeType === 'hide' ?
                ' Skjulte plasser vises ikke på offentlig plasskart.' :
                ' Blokkerte plasser vises grå på offentlig plasskart og kan ikke reserveres.'
              }
              {' '}Reserverte plasser kan ikke endres. Endringer lagres ikke før du trykker "Lagre endringer".
            </div>
          )}

          <div style={{display: 'flex', gap: '20px', marginBottom: '15px', padding: '8px', backgroundColor: '#1a1d29', border: '1px solid #495057', borderRadius: '4px'}}>
            <span style={{color: '#ffffff', fontSize: '14px'}}><strong>Totalt:</strong> {this.state.rows.length}</span>
            <span style={{color: '#dc3545', fontSize: '14px'}}><strong>Reserverte:</strong> {this.state.rows.filter(s => s.isReserved === "yes").length}</span>
            <span style={{color: '#28a745', fontSize: '14px'}}><strong>Betalte:</strong> {this.state.rows.filter(s => s.userInfo && (s.userInfo.paid === true || s.userInfo.paid === "true")).length}</span>
            <span style={{color: '#17a2b8', fontSize: '14px'}}><strong>Ledige:</strong> {this.state.rows.filter(s => s.isReserved !== "yes").length}</span>
            {this.state.editMode && (
              <React.Fragment>
                <span style={{color: '#6c757d', fontSize: '14px'}}><strong>Skjulte:</strong> {this.state.rows.filter(s => s.isHidden).length}</span>
                <span style={{color: '#6c757d', fontSize: '14px'}}><strong>Blokkerte:</strong> {this.state.rows.filter(s => s.isBlocked).length}</span>
              </React.Fragment>
            )}
          </div>

          <div>
            {(this.state.rows === undefined ||
              this.state.rows === null ||
              this.state.rows.length === 0) && <LoadAnim />}
            {this.state.rows !== undefined &&
              this.state.rows !== null &&
              this.state.rows.length !== 0 && (
                <FadeIn>
                  <div className="col-centered seatContainer">
                    <div className="tribune">Tribune</div>
                    <div className="rowDivider"></div>

                    {/* Row 1 */}
                    <div className="row seatRow">
                      <h4 className="rowNum">1</h4>
                      {row1.map((seat, index) => (
                        <div
                          key={index}
                          title={
                            (seat.isReserved === "no" ? "Ledig" : "") ||
                            (seat.isReserved === "yes" ? "Reservert" : "") ||
                            (seat.isReserved === "held" ? "Holdes av " + (seat.reserver || "ukjent") : "")
                          }
                          className={this.getSeatClasses(seat)}
                          onMouseEnter={() => this.handleSeatHover(seat)}
                          onMouseLeave={() => this.handleSeatHover(null)}
                          onClick={() => this.handleSeatClick(seat)}
                        >
                          {seat.number}
                        </div>
                      ))}
                      <h4 className="rowNum">1</h4>
                    </div>

                    {/* Row 2 */}
                    <div className="row seatRow">
                      <h4 className="rowNum">2</h4>
                      {row2.map((seat, index) => (
                        <div
                          key={index}
                          title={
                            (seat.isReserved === "no" ? "Ledig" : "") ||
                            (seat.isReserved === "yes" ? "Reservert" : "") ||
                            (seat.isReserved === "held" ? "Holdes av " + (seat.reserver || "ukjent") : "")
                          }
                          className={this.getSeatClasses(seat)}
                          onMouseEnter={() => this.handleSeatHover(seat)}
                          onMouseLeave={() => this.handleSeatHover(null)}
                          onClick={() => this.handleSeatClick(seat)}
                        >
                          {seat.number}
                        </div>
                      ))}
                      <h4 className="rowNum">2</h4>
                    </div>

                    {/* Divider after row 2 */}
                    <div className="rowDivider"> </div>

                    {/* Row 3 */}
                    <div className="row seatRow">
                      <h4 className="rowNum">3</h4>
                      {row3.map((seat, index) => (
                        <div
                          key={index}
                          title={
                            (seat.isReserved === "no" ? "Ledig" : "") ||
                            (seat.isReserved === "yes" ? "Reservert" : "") ||
                            (seat.isReserved === "held" ? "Holdes av " + (seat.reserver || "ukjent") : "")
                          }
                          className={this.getSeatClasses(seat)}
                          onMouseEnter={() => this.handleSeatHover(seat)}
                          onMouseLeave={() => this.handleSeatHover(null)}
                          onClick={() => this.handleSeatClick(seat)}
                        >
                          {seat.number}
                        </div>
                      ))}
                      <h4 className="rowNum">3</h4>
                    </div>

                    {/* Row 4 */}
                    <div className="row seatRow">
                      <h4 className="rowNum">4</h4>
                      {row4.map((seat, index) => (
                        <div
                          key={index}
                          title={
                            (seat.isReserved === "no" ? "Ledig" : "") ||
                            (seat.isReserved === "yes" ? "Reservert" : "") ||
                            (seat.isReserved === "held" ? "Holdes av " + (seat.reserver || "ukjent") : "")
                          }
                          className={this.getSeatClasses(seat)}
                          onMouseEnter={() => this.handleSeatHover(seat)}
                          onMouseLeave={() => this.handleSeatHover(null)}
                          onClick={() => this.handleSeatClick(seat)}
                        >
                          {seat.number}
                        </div>
                      ))}
                      <h4 className="rowNum">4</h4>
                    </div>

                    {/* Divider after row 4 */}
                    <div className="rowDivider"> </div>

                    {/* Row 5 */}
                    <div className="row seatRow">
                      <h4 className="rowNum">5</h4>
                      {row5.map((seat, index) => (
                        <div
                          key={index}
                          title={
                            (seat.isReserved === "no" ? "Ledig" : "") ||
                            (seat.isReserved === "yes" ? "Reservert" : "") ||
                            (seat.isReserved === "held" ? "Holdes av " + (seat.reserver || "ukjent") : "")
                          }
                          className={this.getSeatClasses(seat)}
                          onMouseEnter={() => this.handleSeatHover(seat)}
                          onMouseLeave={() => this.handleSeatHover(null)}
                          onClick={() => this.handleSeatClick(seat)}
                        >
                          {seat.number}
                        </div>
                      ))}
                      <h4 className="rowNum">5</h4>
                    </div>

                    {/* Row 6 */}
                    <div className="row seatRow">
                      <h4 className="rowNum">6</h4>
                      {row6.map((seat, index) => (
                        <div
                          key={index}
                          title={
                            (seat.isReserved === "no" ? "Ledig" : "") ||
                            (seat.isReserved === "yes" ? "Reservert" : "") ||
                            (seat.isReserved === "held" ? "Holdes av " + (seat.reserver || "ukjent") : "")
                          }
                          className={this.getSeatClasses(seat)}
                          onMouseEnter={() => this.handleSeatHover(seat)}
                          onMouseLeave={() => this.handleSeatHover(null)}
                          onClick={() => this.handleSeatClick(seat)}
                        >
                          {seat.number}
                        </div>
                      ))}
                      <h4 className="rowNum">6</h4>
                    </div>

                    {/* Divider after row 6 */}
                    <div className="rowDivider"> </div>

                    {/* Row 7 */}
                    <div className="row seatRow">
                      <h4 className="rowNum">7</h4>
                      {row7.map((seat, index) => (
                        <div
                          key={index}
                          title={
                            (seat.isReserved === "no" ? "Ledig" : "") ||
                            (seat.isReserved === "yes" ? "Reservert" : "") ||
                            (seat.isReserved === "held" ? "Holdes av " + (seat.reserver || "ukjent") : "")
                          }
                          className={this.getSeatClasses(seat)}
                          onMouseEnter={() => this.handleSeatHover(seat)}
                          onMouseLeave={() => this.handleSeatHover(null)}
                          onClick={() => this.handleSeatClick(seat)}
                        >
                          {seat.number}
                        </div>
                      ))}
                      <h4 className="rowNum">7</h4>
                    </div>

                    {/* Row 8 */}
                    <div className="row seatRow">
                      <h4 className="rowNum">8</h4>
                      {row8.map((seat, index) => (
                        <div
                          key={index}
                          title={
                            (seat.isReserved === "no" ? "Ledig" : "") ||
                            (seat.isReserved === "yes" ? "Reservert" : "") ||
                            (seat.isReserved === "held" ? "Holdes av " + (seat.reserver || "ukjent") : "")
                          }
                          className={this.getSeatClasses(seat)}
                          onMouseEnter={() => this.handleSeatHover(seat)}
                          onMouseLeave={() => this.handleSeatHover(null)}
                          onClick={() => this.handleSeatClick(seat)}
                        >
                          {seat.number}
                        </div>
                      ))}
                      <h4 className="rowNum">8</h4>
                    </div>

                    {/* Divider after row 8 */}
                    <div className="rowDivider"> </div>

                    {/* Row 9 */}
                    <div className="row seatRow">
                      <h4 className="rowNum">9</h4>
                      {row9.map((seat, index) => (
                        <div
                          key={index}
                          title={
                            (seat.isReserved === "no" ? "Ledig" : "") ||
                            (seat.isReserved === "yes" ? "Reservert" : "") ||
                            (seat.isReserved === "held" ? "Holdes av " + (seat.reserver || "ukjent") : "")
                          }
                          className={this.getSeatClasses(seat)}
                          onMouseEnter={() => this.handleSeatHover(seat)}
                          onMouseLeave={() => this.handleSeatHover(null)}
                          onClick={() => this.handleSeatClick(seat)}
                        >
                          {seat.number}
                        </div>
                      ))}
                      <h4 className="rowNum">9</h4>
                    </div>

                    {/* Row 10 */}
                    <div className="row seatRow">
                      <h4 className="rowNum">10</h4>
                      {row10.map((seat, index) => (
                        <div
                          key={index}
                          title={
                            (seat.isReserved === "no" ? "Ledig" : "") ||
                            (seat.isReserved === "yes" ? "Reservert" : "") ||
                            (seat.isReserved === "held" ? "Holdes av " + (seat.reserver || "ukjent") : "")
                          }
                          className={this.getSeatClasses(seat)}
                          onMouseEnter={() => this.handleSeatHover(seat)}
                          onMouseLeave={() => this.handleSeatHover(null)}
                          onClick={() => this.handleSeatClick(seat)}
                        >
                          {seat.number}
                        </div>
                      ))}
                      <h4 className="rowNum">10</h4>
                    </div>

                    <div className="projector">Projector</div>

                    {this.state.hoveredSeat && (
                      <div className="seat-info-box" style={{
                        position: 'fixed',
                        bottom: '20px',
                        right: '20px',
                        backgroundColor: '#1a1d29',
                        color: 'white',
                        padding: '15px',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
                        border: '1px solid #495057',
                        zIndex: 1000,
                        minWidth: '250px'
                      }}>
                        <h4 style={{margin: '0 0 10px 0', color: 'white', borderBottom: '1px solid #495057', paddingBottom: '8px'}}>
                          Plass {this.state.hoveredSeat.rownum}-{this.state.hoveredSeat.number}
                        </h4>
                        <p style={{margin: '5px 0', color: 'white'}}>
                          <strong>Status:</strong> {
                            this.state.hoveredSeat.isReserved === "yes" ? "Reservert" :
                            this.state.hoveredSeat.isReserved === "held" ? "Midlertidig holdt" : "Ledig"
                          }
                        </p>
                        {this.state.hoveredSeat.userInfo && (
                          <div>
                            <p style={{margin: '5px 0', color: 'white'}}>
                              <strong>Navn:</strong> {this.state.hoveredSeat.userInfo.firstName} {this.state.hoveredSeat.userInfo.lastName}
                            </p>
                            {this.state.hoveredSeat.userInfo.age && (
                              <p style={{margin: '5px 0', color: 'white'}}>
                                <strong>Alder:</strong> {this.state.hoveredSeat.userInfo.age}
                              </p>
                            )}
                            <p style={{margin: '5px 0', color: (this.state.hoveredSeat.userInfo.paid === true || this.state.hoveredSeat.userInfo.paid === "true") ? '#28a745' : '#dc3545'}}>
                              <strong>Betalt:</strong> {(this.state.hoveredSeat.userInfo.paid === true || this.state.hoveredSeat.userInfo.paid === "true") ? 'Ja' : 'Nei'}
                            </p>
                            <p style={{margin: '5px 0', color: 'white'}}>
                              <strong>Email:</strong> {this.state.hoveredSeat.userInfo.email}
                            </p>
                          </div>
                        )}
                        {this.state.hoveredSeat.reserver && !this.state.hoveredSeat.userInfo && (
                          <p style={{margin: '5px 0', color: 'white'}}>
                            <strong>Reservert av:</strong> {this.state.hoveredSeat.reserver}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </FadeIn>
              )}
          </div>

          {/* Info Modal */}
          {this.state.showInfoModal && this.state.infoModalSeat && (
            <div style={{
              position: 'fixed',
              top: '0',
              left: '0',
              width: '100%',
              height: '100%',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 2000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }} onClick={this.closeInfoModal}>
              <div style={{
                backgroundColor: '#1a1d29',
                color: 'white',
                padding: '30px',
                borderRadius: '10px',
                boxShadow: '0 8px 16px rgba(0, 0, 0, 0.3)',
                border: '1px solid #495057',
                minWidth: '400px',
                maxWidth: '500px'
              }} onClick={(e) => e.stopPropagation()}>
                <h3 style={{margin: '0 0 20px 0', color: 'white', borderBottom: '1px solid #495057', paddingBottom: '15px'}}>
                  Plass {this.state.infoModalSeat.rownum}-{this.state.infoModalSeat.number}
                </h3>

                <div style={{marginBottom: '15px'}}>
                  <strong>Status:</strong> {
                    this.state.infoModalSeat.isReserved === "yes" ? "Reservert" :
                    this.state.infoModalSeat.isReserved === "held" ? "Midlertidig holdt" : "Ledig"
                  }
                </div>

                {this.state.infoModalSeat.userInfo && (
                  <div>
                    <div style={{marginBottom: '10px'}}>
                      <strong>Navn:</strong>
                      <input
                        type="text"
                        value={`${this.state.infoModalSeat.userInfo.firstName} ${this.state.infoModalSeat.userInfo.lastName}`}
                        readOnly
                        style={{
                          marginLeft: '10px',
                          padding: '5px',
                          border: '1px solid #495057',
                          borderRadius: '3px',
                          backgroundColor: '#343a40',
                          color: 'white',
                          width: '250px'
                        }}
                      />
                    </div>

                    <div style={{marginBottom: '10px'}}>
                      <strong>Bestillingsnummer:</strong>
                      <input
                        type="text"
                        value={this.state.infoModalSeat.userInfo.code || 'N/A'}
                        readOnly
                        style={{
                          marginLeft: '10px',
                          padding: '5px',
                          border: '1px solid #495057',
                          borderRadius: '3px',
                          backgroundColor: '#343a40',
                          color: 'white',
                          width: '120px'
                        }}
                      />
                    </div>

                    <div style={{marginBottom: '10px'}}>
                      <strong>Email:</strong>
                      <input
                        type="text"
                        value={this.state.infoModalSeat.userInfo.email || 'N/A'}
                        readOnly
                        style={{
                          marginLeft: '10px',
                          padding: '5px',
                          border: '1px solid #495057',
                          borderRadius: '3px',
                          backgroundColor: '#343a40',
                          color: 'white',
                          width: '250px'
                        }}
                      />
                    </div>

                    <div style={{marginBottom: '10px'}}>
                      <strong>Telefon:</strong>
                      <input
                        type="text"
                        value={this.state.infoModalSeat.userInfo.phone || 'N/A'}
                        readOnly
                        style={{
                          marginLeft: '10px',
                          padding: '5px',
                          border: '1px solid #495057',
                          borderRadius: '3px',
                          backgroundColor: '#343a40',
                          color: 'white',
                          width: '150px'
                        }}
                      />
                    </div>

                    {this.state.infoModalSeat.userInfo.age && (
                      <div style={{marginBottom: '10px'}}>
                        <strong>Alder:</strong>
                        <input
                          type="text"
                          value={this.state.infoModalSeat.userInfo.age}
                          readOnly
                          style={{
                            marginLeft: '10px',
                            padding: '5px',
                            border: '1px solid #495057',
                            borderRadius: '3px',
                            backgroundColor: '#343a40',
                            color: 'white',
                            width: '60px'
                          }}
                        />
                      </div>
                    )}

                    <div style={{marginBottom: '15px', color: (this.state.infoModalSeat.userInfo.paid === true || this.state.infoModalSeat.userInfo.paid === "true") ? '#28a745' : '#dc3545'}}>
                      <strong>Betalt:</strong> {(this.state.infoModalSeat.userInfo.paid === true || this.state.infoModalSeat.userInfo.paid === "true") ? 'Ja' : 'Nei'}
                    </div>
                  </div>
                )}

                <div style={{textAlign: 'center', marginTop: '20px'}}>
                  <button
                    onClick={this.closeInfoModal}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#6c757d',
                      color: 'white',
                      border: 'none',
                      borderRadius: '5px',
                      cursor: 'pointer'
                    }}
                  >
                    Lukk
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      );
    }
  }
);


// WEBPACK FOOTER //
// ./src/Components/Admin/SeatmapAdmin.js